<script setup>
/**
 * 公会金库（/guilds/:slug/bank，负责人与运营）：余额、本月预算，和一张进出存流水表。
 * 申请充值、从自己的 Agent 转入这两个操作都是表头的按钮，各开一个弹窗；充值申请批准前以「待批」行出现在表里。
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Badge, Button, Input, Modal, Select, Table } from '@drama/ui'

import { api, loadAccount, session } from '../api.js'

const { t, te } = useI18n()
const route = useRoute()
const base = `/api/voko/guilds/${encodeURIComponent(route.params.slug)}`
const guild = ref(null)
const members = ref([])
const bank = ref(null)
const mine = ref([])
const message = ref('')
const error = ref('')
const dialog = ref('')
const topup = reactive({ amount: '', note: '' })
const move = reactive({ agent_id: '', amount: '', key: crypto.randomUUID() })

/** 负责人的公司 Agent 一定在册：公司 Agent 的主人就是负责人。 */
const isLead = computed(() => session.user !== null && members.value.some((member) => member.role === 'company' && member.owner_handle === session.user.handle))
const agentOptions = computed(() => mine.value.filter((agent) => agent.status === 'active').map((agent) => ({ value: agent.id, label: t('guilds.manageForm.bank.agentOption', { name: agent.full_name, available: agent.wallet.available }) })))
const columns = computed(() => [
  { key: 'at', label: t('guildPages.common.time'), width: '180px' },
  { key: 'reason', label: t('guildPages.bank.reason'), width: '360px', wrap: true },
  { key: 'amount', label: t('guildPages.bank.amount'), width: '120px', align: 'right' },
])
const when = (at) => new Date(at).toLocaleString()
/** 待批的充值申请排在最前，之后是流水（新到旧）。 */
const rows = computed(() => (bank.value === null ? [] : [
  ...bank.value.topup_requests.map((item) => ({ id: item.id, at: item.created_at, reason: t('guildPages.bank.pendingReason', { note: item.note }), amount: item.amount, pending: true })),
  ...bank.value.ledger.map((item) => ({ id: item.id, at: item.at, reason: te(`ledger.reason.${item.reason}`) ? t(`ledger.reason.${item.reason}`) : item.reason, amount: item.amount, pending: false })),
]))

async function load() {
  const [data, bankData, agentData] = await Promise.all([api('GET', `${base}/manage`), api('GET', `${base}/bank`), api('GET', '/api/voko/agents')])
  guild.value = data.guild
  members.value = data.members
  bank.value = bankData
  mine.value = agentData.agents
}
onMounted(async () => {
  try { await load() } catch (err) { error.value = err instanceof Error ? err.message : String(err) }
})

async function run(action, done) {
  message.value = ''
  error.value = ''
  try {
    await action()
    dialog.value = ''
    await Promise.all([load(), loadAccount()])
    message.value = done
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}
const requestTopup = () => run(async () => {
  await api('POST', `${base}/bank/topup-requests`, { amount: Number(topup.amount), note: topup.note })
  Object.assign(topup, { amount: '', note: '' })
}, t('guilds.manageForm.bank.topupRequested'))
// 一次转入一个 Idempotency-Key：网络重试不会扣两次；成功后换新的。
const transfer = () => run(async () => {
  await api('POST', `${base}/bank/transfers`, { agent_id: move.agent_id, amount: Number(move.amount) }, move.key)
  Object.assign(move, { agent_id: '', amount: '', key: crypto.randomUUID() })
}, t('guilds.manageForm.bank.transferred'))
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guildBank') }}</p>
      <h1 class="page-head__title">{{ t('guilds.manageForm.bank.title') }}{{ guild === null ? '' : ` · ${guild.name}` }}</h1>
    </div>
  </header>
  <p v-if="message" role="status">{{ message }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <p v-if="guild !== null && guild.status === 'archived'">{{ t('guilds.manageForm.archivedNote') }}</p>
  <section v-else-if="bank !== null" class="voko-panel" :aria-label="t('guilds.manageForm.bank.title')">
    <p class="voko-muted">{{ t('guilds.manageForm.bank.intro') }}</p>
    <p class="gb-balance">{{ t('guilds.manageForm.bank.balance', bank.bank) }}</p>
    <p>{{ bank.monthly_budget_limit === null ? t('guilds.manageForm.bank.monthlyUnlimited', { used: bank.monthly_budget_used }) : t('guilds.manageForm.bank.monthly', { used: bank.monthly_budget_used, limit: bank.monthly_budget_limit }) }}</p>
    <div v-if="isLead" class="gb-actions">
      <Button variant="secondary" @click="dialog = 'topup'">{{ t('guilds.manageForm.bank.topupTitle') }}</Button>
      <Button variant="secondary" @click="dialog = 'transfer'">{{ t('guilds.manageForm.bank.transferTitle') }}</Button>
    </div>
    <h2 class="gb-title">{{ t('guilds.manageForm.bank.ledgerTitle') }}</h2>
    <Table :columns="columns" :rows="rows">
      <template #at="{ row }">{{ when(row.at) }}</template>
      <template #reason="{ row }">{{ row.reason }} <Badge v-if="row.pending" size="sm">{{ t('guildPages.bank.pending') }}</Badge></template>
      <template #amount="{ row }"><strong :class="row.pending ? '' : row.amount > 0 ? 'gb-in' : 'gb-out'">{{ row.amount > 0 ? `+${row.amount}` : row.amount }}</strong></template>
    </Table>
    <p v-if="rows.length === 0" class="voko-muted">{{ t('guilds.manageForm.bank.noLedger') }}</p>
  </section>

  <Modal :open="dialog === 'topup'" :title="t('guilds.manageForm.bank.topupTitle')" width="440px" @close="dialog = ''">
    <form class="gb-form" @submit.prevent="requestTopup">
      <p class="voko-muted">{{ t('guildPages.bank.topupHint') }}</p>
      <Input v-model="topup.amount" type="number" min="1" :aria-label="t('guilds.manageForm.bank.amount')" :placeholder="t('guilds.manageForm.bank.amount')" />
      <Input v-model="topup.note" maxlength="500" :aria-label="t('guilds.manageForm.bank.note')" :placeholder="t('guilds.manageForm.bank.note')" />
      <div class="gb-actions">
        <Button type="button" variant="ghost" @click="dialog = ''">{{ t('guildPages.common.cancel') }}</Button>
        <Button type="submit" :disabled="topup.amount === '' || topup.note.trim() === ''">{{ t('guilds.manageForm.bank.requestTopup') }}</Button>
      </div>
    </form>
  </Modal>
  <Modal :open="dialog === 'transfer'" :title="t('guilds.manageForm.bank.transferTitle')" width="440px" @close="dialog = ''">
    <form v-if="agentOptions.length > 0" class="gb-form" @submit.prevent="transfer">
      <p class="voko-muted">{{ t('guilds.manageForm.bank.transferIntro') }}</p>
      <Select v-model="move.agent_id" :options="agentOptions" :placeholder="t('guilds.manageForm.bank.pickAgent')" :aria-label="t('guilds.manageForm.bank.pickAgent')" />
      <Input v-model="move.amount" type="number" min="1" :aria-label="t('guilds.manageForm.bank.amount')" :placeholder="t('guilds.manageForm.bank.amount')" />
      <div class="gb-actions">
        <Button type="button" variant="ghost" @click="dialog = ''">{{ t('guildPages.common.cancel') }}</Button>
        <Button type="submit" :disabled="move.agent_id === '' || move.amount === ''">{{ t('guilds.manageForm.bank.transfer') }}</Button>
      </div>
    </form>
    <p v-else class="voko-muted">{{ t('guilds.manageForm.bank.noAgents') }}</p>
  </Modal>
</template>

<style scoped>
.voko-panel { margin-bottom: var(--drama-space-lg); }
.gb-balance { font-size: var(--drama-font-size-lg); font-weight: 600; }
.gb-title { margin: var(--drama-space-md) 0 var(--drama-space-xs); font-size: var(--drama-font-size-md); }
.gb-actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); margin: var(--drama-space-sm) 0; }
.gb-form { display: grid; gap: var(--drama-space-sm); }
.gb-in { color: var(--drama-color-success); }
.gb-out { color: var(--drama-color-text-muted); }
</style>
